import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { fetchEventsByName } from "../PhotoManager/API/api";
import { getEncodedEntityPath } from "../PhotoManager/components/FolderFeatures/EncryptionOfFolder";

// Import sidebar icons
import folderIcon from "../PhotoManager/styles/images/folder-icon.svg";
import eventIcon from "../PhotoManager/styles/images/Event.svg";
import albumIcon from "../PhotoManager/styles/images/gallery.svg";

const EventSearchBar = ({
  placeholder,
  isMobile = false
}: {
  placeholder: string;
  isMobile: boolean;
}) => {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);

  // Generate proper navigation URL based on entity type
  const generateEntityURL = (entity: any) => {
    const { entityType, id } = entity;

    switch (entityType) {
      case "Gallery":
        const galleryPath = `g:${id}`;
        const encodedGalleryPath = getEncodedEntityPath(galleryPath);
        return `/library?p=${encodedGalleryPath}`;

      case "Album":
        // For albums, we need to include the parent event in the path
        if (entity.eventId) {
          const albumPath = `g:${entity.eventId}/a:${id}`;
          const encodedAlbumPath = getEncodedEntityPath(albumPath);
          return `/library?p=${encodedAlbumPath}`;
        }
        // Fallback to albumPath (secure_path) if available
        if (entity.albumPath) {
          return entity.albumPath;
        }
        // Final fallback to library
        return `/library`;

      case "LibraryFolder":
        const folderPath = `f:${id}`;
        const encodedFolderPath = getEncodedEntityPath(folderPath);
        return `/library?p=${encodedFolderPath}`;

      default:
        // Fallback to library for safety
        return `/library`;
    }
  };

  // Handle navigation programmatically to stay within SPA
  const handleEntityClick = (entity: any, event: React.MouseEvent) => {
    event.preventDefault();
    const url = generateEntityURL(entity);
    window.history.pushState({}, "", url);

    // Close search dropdown by clearing query and results
    setQuery("");
    setSearchResults([]);
    setNoResults(false);

    // Trigger a custom event to notify PhotoManager of URL change
    window.dispatchEvent(new Event("popstate"));
  };

  // Get CSS class for entity type badge
  const getEntityBadgeClass = (entityType: string) => {
    switch (entityType) {
      case "Gallery":
        return "gallery";
      case "Album":
        return "album";
      case "LibraryFolder":
        return "library-folder";
      default:
        return "";
    }
  };

  // Get icon for entity type (same as sidebar)
  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case "Gallery":
        return eventIcon;
      case "Album":
        return albumIcon;
      case "LibraryFolder":
        return folderIcon;
      default:
        return eventIcon; // fallback to event icon
    }
  };

  // Get the name for the entity badge
  const getEntityBadgeName = (entityType: string) => {
    switch (entityType) {
      case "Gallery":
        return "Event";
      case "Album":
        return "Album";
      case "LibraryFolder":
        return "Folder";
      default:
        return "";
    }
  };

  useEffect(() => {
    $("#close-search-only-nav").on("click", function () {
      $("#mobile-nav-with-only-search").hide().removeClass("only-search-show");
      $("#mobile-nav-with-icons").show();
    });

    // Cleanup the event listener when the component unmounts
    return () => {
      $("#close-search-only-nav").off("click");
    };
  }, []);

  const fetchDebouncedResults = useCallback(
    debounce(async searchTerm => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setNoResults(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const events = await fetchEventsByName(searchTerm);
      setSearchResults(events.entities);
      setNoResults(events.entities.length === 0);
      setIsLoading(false);
    }, 700),
    []
  );
  useEffect(() => {
    fetchDebouncedResults(query);
  }, [query, fetchDebouncedResults]);

  return (
    <div className="top-bar-search">
      <div
        className={`v3-search-container ${
          searchResults.length > 0 || noResults
            ? isMobile
              ? "search-border-bottom"
              : "search-border-full"
            : null
        }`}
      >
        {isMobile ? (
          <div className="search-highlighter">
            <input
              type="text"
              placeholder={placeholder}
              className="search-bar"
              value={query}
              onChange={e => setQuery(e.target.value)}
              id="event-search-mobile"
            />
            <div id="close-search-only-nav" className="search-close">
              <img
                alt="close-search"
                src="/images/website/home/close.svg"
                className="close-search-nav-icon"
              ></img>
            </div>
            {isLoading ? (
              <div className="v3-spinner-mob">
                <span className="fa fa-spinner fa-spin"></span>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder={placeholder}
              className="search-bar"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {isLoading ? (
              <div className="v3-search-loading-spinner">
                <span className="fa fa-spinner fa-spin"></span>
              </div>
            ) : null}
          </>
        )}
        {noResults && !isLoading && (
          <ul className="search-results">
            <p className="no-results-text">No search results found</p>
          </ul>
        )}

        {searchResults.length > 0 ? (
          <ul className="search-results">
            {searchResults.map((entity: any) => (
              <li
                key={`${entity.entityType}-${entity.id}`}
                className="search-result-item"
              >
                <a
                  href={generateEntityURL(entity)}
                  onClick={e => handleEntityClick(entity, e)}
                >
                  <div className="event-title">
                    <img
                      src={getEntityIcon(entity.entityType)}
                      alt={entity.entityType}
                      className="search-result-icon"
                    />
                    <div className="event-content">
                      <div className="event-ptitle">
                        {entity.name}
                        <span
                          className={`entity-type-badge ${getEntityBadgeClass(
                            entity.entityType
                          )}`}
                        >
                          {getEntityBadgeName(entity.entityType)}
                        </span>
                      </div>
                      <div className="event-stitle">
                        {entity.date && (
                          <>
                            {entity.date}
                            {entity.location && (
                              <>
                                <span className="event-desc-divider">•</span>{" "}
                                {entity.location}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default EventSearchBar;
